//Librerías
import { useCallback, useEffect, useState } from "react";
import { Alert, Button, Col, Container, Form, Row, Spinner } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";

//Componentes
import useFetch from "../../../hooks/useFetch";
import { Controller, useForm } from "react-hook-form";
import Select from "react-select";
import { useGetSucursalesByPlataforma } from "../../../hooks/useGetSucursalesByPlataforma";

//interfaces
import { ActividadShow } from "../../../interfaces/ActividadShowInterface";
import { SucursalInterface } from "../../../interfaces/SucursalInterface";
import { EstadoInterface } from "../../../interfaces/EstadoInterface";

//estilos (reutiliza el del edit completo)
const classes = require("./ServicioEdit.module.css").default;

interface Contacto {
  id: number;
  nombre: string;
  correo: string;
  servicio_id: number;
  telefono_movil: string;
  telefono_fijo: string;
}

/**
 * ServicioEditBasico Component
 * @description: Edición reducida para el rol "Servicios" (cliente). Permite
 *   editar solo Datos generales (folio) + Datos de la sucursal + Contactos.
 *   NO muestra ni consulta datos de unidad/tipo de servicio, y NO los envía,
 *   de modo que la unidad placeholder (id 0) nunca se modifica.
 */
const ServicioEditBasico = () => {
  const { idActividad } = useParams();
  const { sendRequest } = useFetch();
  const [actividad, setActividad] = useState<ActividadShow>();
  const navigate = useNavigate();
  const [contacto, setContacto] = useState<Contacto[]>([]);
  const [estados, setEstados] = useState<EstadoInterface[]>();

  const { data: sucursales, isLoading: sucursalesLoading } =
    useGetSucursalesByPlataforma();

  let currentContacto: Contacto[] = [];
  let bodyPost = {};

  useEffect(() => {
    sendRequest({ url: `/estado` }, (variable: any) => {
      setEstados(
        variable.data.map((estado: EstadoInterface) => {
          return { value: estado.id, label: estado.nombre };
        })
      );
    });
  }, [sendRequest]);

  const formSchema = Yup.object().shape({
    folio: Yup.string().nullable().optional(),
    sucursal: Yup.object().required("Es necesario seleccionar la sucursal"),
    domicilio_sucursal: Yup.string()
      .nullable()
      .optional()
      .max(200, "El domicilio no puede ser mayor a 200 caracteres"),
    estado: Yup.object().required("Es necesario seleccionar un estado"),
    latitud_sucursal: Yup.string().required("La latitud es obligatoria"),
    longitud_sucursal: Yup.string().required("La longitud es obligatoria"),
    notas_sucursal: Yup.string()
      .nullable()
      .optional()
      .max(200, "Las observaciones no puede ser mayores a 200 caracteres"),
  });

  const {
    register,
    handleSubmit,
    reset,
    control,
    setValue,
    formState: { errors },
  } = useForm({
    mode: "onTouched",
    resolver: yupResolver(formSchema),
  });

  const transformData = useCallback(
    (variable: any) => {
      setActividad(variable.data);
      const contactos = variable.data.servicio.contacto;
      setContacto(
        contactos.length > 0
          ? contactos
          : [
              {
                id: 0,
                nombre: "",
                correo: "",
                servicio_id: variable.data.servicio.id,
                telefono_movil: "",
                telefono_fijo: "",
              },
            ]
      );
      reset({
        sucursal: {
          value: variable.data.servicio.sucursal.id,
          label: variable.data.servicio.sucursal.nombre,
        },
        estado: {
          value: variable.data.servicio.estado.id,
          label: variable.data.servicio.estado.nombre,
        },
        domicilio_sucursal: variable.data.servicio.domicilio_sucursal,
        latitud_sucursal: variable.data.servicio.latitud_sucursal,
        longitud_sucursal: variable.data.servicio.longitud_sucursal,
        notas_sucursal: variable.data.servicio.notas_sucursal,
        folio: variable.data.servicio.folio,
      });
    },
    [reset]
  );

  useEffect(() => {
    sendRequest(
      { url: `/actividad/${idActividad}?includeAll=true` },
      transformData
    );
  }, [idActividad, sendRequest, transformData]);

  // Guarda solo servicio + contactos. NO envía unidad ni tipo_servicio,
  // por lo que el backend (con el ajuste defensivo) no toca la unidad placeholder.
  const editServicio = (data: any) => {
    bodyPost = {
      actividad_id: actividad?.id,
      ...data,
      contacto: [...contacto],
      editar: true,
    };
    sendRequest(
      {
        url: `/servicio/${actividad?.servicio.id}`,
        method: "PATCH",
        body: bodyPost,
      },
      (variable: any) => {
        toast.success("Se editó el servicio correctamente", {
          position: "top-left",
          autoClose: 5000,
          theme: "light",
        });
        navigate(-1);
      }
    );
  };

  if (!actividad || sucursalesLoading) {
    return (
      <div className={classes.spinnerContainer}>
        <Spinner animation="grow" variant="info" />
      </div>
    );
  }

  return (
    <Container>
      <h1>Editar el servicio {idActividad}</h1>
      <h2 className={classes.subtitulo}>Datos generales </h2>
      <Form onSubmit={handleSubmit(editServicio)}>
        <Row className="mb-3">
          <Form.Group as={Col} xs={6}>
            <Form.Label>Folio</Form.Label>
            <Form.Control
              {...register("folio", { maxLength: 50 })}
              placeholder="Ingresar el folio de órden de venta"
            />
            {errors.folio && (
              <Form.Text style={{ color: "red" }}>
                {errors.folio?.message}
              </Form.Text>
            )}
          </Form.Group>
        </Row>

        <h2 className={classes.subtitulo}>Datos de la sucursal </h2>
        <Row className="mb-3">
          <Form.Group as={Col}>
            <Form.Label>Sucursal</Form.Label>
            <Controller
              name="sucursal"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  options={sucursales?.map((sucursal: SucursalInterface) => {
                    return {
                      value: sucursal.id,
                      label: sucursal.nombre,
                      domicilio: sucursal.domicilio,
                      latitud: sucursal.latitud,
                      longitud: sucursal.longitud,
                      estado: sucursal.estado,
                    };
                  })}
                  onChange={(sucursal: any) => {
                    setValue("sucursal", {
                      value: sucursal.value,
                      label: sucursal.label,
                    });
                    setValue("domicilio_sucursal", sucursal.domicilio);
                    setValue("latitud_sucursal", sucursal.latitud);
                    setValue("longitud_sucursal", sucursal.longitud);
                    setValue("estado", {
                      value: sucursal.estado.id,
                      label: sucursal.estado.nombre,
                    });
                  }}
                  placeholder="Seleccionar..."
                />
              )}
            />
            {errors.sucursal && (
              <Form.Text style={{ color: "red" }}>
                {errors.sucursal?.message}
              </Form.Text>
            )}
          </Form.Group>
          <Form.Group as={Col}>
            <Form.Label>Domicilio</Form.Label>
            <Form.Control {...register("domicilio_sucursal", { maxLength: 200 })} />
            {errors.domicilio_sucursal && (
              <Form.Text style={{ color: "red" }}>
                {errors.domicilio_sucursal?.message}
              </Form.Text>
            )}
          </Form.Group>

          <Form.Group as={Col}>
            <Form.Label>Estado</Form.Label>
            <Controller
              name="estado"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  options={estados}
                  placeholder="Seleccionar..."
                />
              )}
            />
            {errors.estado && (
              <Form.Text style={{ color: "red" }}>
                {errors.estado?.message}
              </Form.Text>
            )}
          </Form.Group>
        </Row>
        <Row className="mb-3">
          <Form.Group as={Col}>
            <Form.Label>Latitud</Form.Label>
            <Form.Control
              type="number"
              step={0.00000000001}
              min={-90}
              max={90}
              {...register("latitud_sucursal", {
                maxLength: 50,
                required: true,
              })}
            />
            {errors.latitud_sucursal && (
              <Form.Text style={{ color: "red" }}>
                {errors.latitud_sucursal?.message}
              </Form.Text>
            )}
          </Form.Group>
          <Form.Group as={Col}>
            <Form.Label>Longitud</Form.Label>
            <Form.Control
              type="number"
              step={0.00000000001}
              min={-180}
              max={180}
              {...register("longitud_sucursal", {
                maxLength: 50,
                required: true,
              })}
            />
            {errors.longitud_sucursal && (
              <Form.Text style={{ color: "red" }}>
                {errors.longitud_sucursal?.message}
              </Form.Text>
            )}
          </Form.Group>

          <Form.Group as={Col}>
            <Form.Label>Observaciones</Form.Label>
            <Form.Control
              {...register("notas_sucursal", { maxLength: 200 })}
              as="textarea"
            />
            {errors.notas_sucursal && (
              <Form.Text style={{ color: "red" }}>
                {errors.notas_sucursal?.message}
              </Form.Text>
            )}
          </Form.Group>
        </Row>
        <Alert variant="secondary">
          <Alert.Heading>Datos de contacto</Alert.Heading>
          {contacto.map((contact: any, index) => {
            return (
              <Row key={index} className="mb-3">
                <Form.Group as={Col}>
                  <Form.Label>Nombre del contacto</Form.Label>
                  <Form.Control
                    value={contact.nombre as string}
                    onChange={(event) => {
                      currentContacto = contacto;
                      currentContacto[index].nombre = event.target.value;
                      setContacto([...currentContacto]);
                    }}
                  />
                </Form.Group>

                <Form.Group as={Col}>
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    value={contact.correo as string}
                    onChange={(event) => {
                      currentContacto = contacto;
                      currentContacto[index].correo = event.target.value;
                      setContacto([...currentContacto]);
                    }}
                  />
                </Form.Group>

                <Form.Group as={Col}>
                  <Form.Label>Teléfono móvil</Form.Label>
                  <Form.Control
                    value={contact.telefono_movil as string}
                    onChange={(event) => {
                      currentContacto = contacto;
                      currentContacto[index].telefono_movil = event.target.value;
                      setContacto([...currentContacto]);
                    }}
                  />
                </Form.Group>

                <Form.Group as={Col}>
                  <Form.Label>Teléfono fijo</Form.Label>
                  <Form.Control
                    value={
                      contact.telefono_fijo
                        ? (contact.telefono_fijo as string)
                        : ""
                    }
                    onChange={(event) => {
                      currentContacto = contacto;
                      currentContacto[index].telefono_fijo = event.target.value;
                      setContacto([...currentContacto]);
                    }}
                  />
                </Form.Group>
              </Row>
            );
          })}
        </Alert>

        <Button
          style={{
            backgroundColor: "#21618C",
            borderColor: "white",
            marginTop: "10px",
          }}
          type="submit"
        >
          Guardar
        </Button>
      </Form>
    </Container>
  );
};

export default ServicioEditBasico;
